import { combine, createEffect, createEvent, createStore } from 'effector';
import { IFeaturing, IFeaturingCreate } from '@interfaces/IFeaturing';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { closeModal } from '@stores/modals';
import createPreloadersStore from '@stores/preloaders';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from '@stores/pagination';
import { get } from 'lodash';
import update from 'immutability-helper';

export const reset = createEvent('Featuring store reset');

export const createFeaturing = createEffect({
  name: 'createFeaturing',
  async handler(data: IFeaturingCreate) {
    return await request(api.featuring.create, { data });
  },
});

export const editFeaturing = createEffect({
  name: 'editFeaturing',
  async handler({ data, id }: { data: IFeaturingCreate; id: string }) {
    return await request(api.featuring.update, {
      data,
      urlParams: { promotionId: id },
    });
  },
});

export const getList = createEffect({
  name: 'getList',
  async handler(params?: { pagination?: PaginationProps; sortType?: string; active?: string }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    const { current } = paginationParams;

    const requestParams = {
      paged: true,
      size: localStorage.getItem('featuringPageSize'),
      page: (current || 1) - 1,
      active: params?.active || 'TRUE',
      sort: [`startDate,${params?.sortType || 'desc'}`],
    };

    return await request(api.featuring.getList, { params: requestParams });
  },
});

export const block = createEffect({
  name: 'block',
  async handler({ featuringId }: { featuringId: string; preloaderId: string }) {
    return await request(api.featuring.block, {
      urlParams: {
        promotionId: featuringId,
      },
    });
  },
});

export const unblock = createEffect({
  name: 'unblock',
  async handler({ featuringId }: { featuringId: string; preloaderId: string }) {
    return await request(api.featuring.unblock, {
      urlParams: {
        promotionId: featuringId,
      },
    });
  },
});

const preloaders = createPreloadersStore([createFeaturing, getList, editFeaturing, block, unblock]);

const { pagination } = createPaginationStore({
  getList,
  listName: 'featuring',
  resetEvent: reset,
  bodyPath: ['data'],
  syncWithQuery: true,
});

export const blockHandler = (
  state: IFeaturing[],
  {
    result: { data },
    params: { featuringId },
  }: { result: { data: { body: IFeaturing } }; params: { featuringId: string } },
) => {
  const index = state.findIndex((p) => p.id.toString() === featuringId);
  if (index >= 0) {
    const updatedState: any = update(state, {
      $splice: [[index, 1, data.body]],
    });
    return updatedState;
  } else {
    return state;
  }
};

const list = createStore<IFeaturing[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'content'], []))
  .on(createFeaturing.done, (state, { result }) => {
    closeModal({ name: 'featuring' });
    getList();
  })
  .on(editFeaturing.done, (state, { result, params }) => {
    closeModal({ name: 'featuring' });
    getList({ pagination: pagination.getState() });
  })
  .on(block.done, blockHandler)
  .on(unblock.done, blockHandler)
  .reset(reset);

const featuring = combine({ preloaders, list, pagination });

export default featuring;
